import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, RefreshCw, Calendar, Users, ChevronDown, ChevronUp,
  AlertCircle, Clock, ExternalLink,
} from 'lucide-react';
import { T, ROLES, springs } from '../lib/tokens';
import { isLocalAvailable, apiFetch } from '../lib/api';
import { Glass } from '../components/glass/Glass';
import { Md } from '../components/primitives/Md';

const fmtMeetingTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMs = d - now;
  const diffH = diffMs / 3600000;
  const sameDay = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now); tomorrow.setDate(now.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  const time = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });

  let prefix;
  if (diffH < 0) prefix = 'now';
  else if (diffH < 1) prefix = `in ${Math.round(diffH * 60)}m`;
  else if (sameDay) prefix = `today, ${time}`;
  else if (isTomorrow) prefix = `tomorrow, ${time}`;
  else prefix = d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) + `, ${time}`;
  return prefix;
};

const ACCOUNT_TO_ROLE = { liken: 'liken', oneday: 'oneday', michaelpryke: 'pryke' };

const BriefRow = ({ event, savedBrief, onGenerate, busy }) => {
  const [open, setOpen] = useState(false);
  const role = ROLES[ACCOUNT_TO_ROLE[event.gcal_account]] || null;
  const hasBrief = !!savedBrief?.brief_markdown;

  // Auto-open if brief exists
  useEffect(() => {
    if (hasBrief && open === false && !savedBrief.__userToggled) {
      // initial render only — leave subsequent toggles to user
    }
  }, [hasBrief]);

  const toggle = () => setOpen(o => !o);

  return (
    <Glass depth={1} hover={false} style={{ padding: 0, marginBottom: 12, overflow: 'hidden' }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '160px 1fr auto',
        gap: 16,
        padding: '14px 18px',
        alignItems: 'center',
      }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: T.text, letterSpacing: '-0.01em' }}>
            {fmtMeetingTime(event.meeting_start)}
          </div>
          {role && (
            <span style={{
              display: 'inline-block', marginTop: 4,
              fontSize: 9, fontWeight: 600, color: role.color, background: role.light,
              padding: '2px 6px', borderRadius: 5, letterSpacing: '0.04em',
            }}>{role.label}</span>
          )}
        </div>

        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.text, marginBottom: 4 }}>
            {event.title}
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <Users size={11} />
            {event.attendees?.length ? (
              event.attendees.slice(0, 5).map((a, i) => (
                <span key={i}>{a.name || a.email}{i < Math.min(event.attendees.length, 5) - 1 ? ',' : ''}</span>
              ))
            ) : (
              <span>no external attendees</span>
            )}
            {event.attendees?.length > 5 && <span>+{event.attendees.length - 5} more</span>}
            {event.html_link && (
              <a href={event.html_link} target="_blank" rel="noopener noreferrer" style={{
                color: T.blue, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 3, marginLeft: 'auto',
              }}>
                gcal <ExternalLink size={10} />
              </a>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          {hasBrief ? (
            <>
              <button onClick={() => onGenerate(event.gcal_event_id, true)} disabled={busy} title="Regenerate" style={{
                background: 'rgba(0,0,0,0.04)', border: 'none', borderRadius: 8, cursor: 'pointer',
                padding: '6px 8px', color: T.textMid, opacity: busy ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <RefreshCw size={12} className={busy ? 'spin' : ''} />
              </button>
              <button onClick={toggle} style={{
                background: T.amberLight, border: 'none', borderRadius: 8, cursor: 'pointer',
                padding: '6px 12px', color: T.amber, fontSize: 12, fontWeight: 600,
                display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'inherit',
              }}>
                {open ? <><ChevronUp size={12} /> Hide</> : <><ChevronDown size={12} /> View</>}
              </button>
            </>
          ) : (
            <button onClick={() => onGenerate(event.gcal_event_id, false)} disabled={busy || !event.attendees?.length || !isLocalAvailable()} style={{
              background: T.violetLight, border: 'none', borderRadius: 8, cursor: 'pointer',
              padding: '6px 12px', color: T.violet, fontSize: 12, fontWeight: 600,
              opacity: (busy || !event.attendees?.length || !isLocalAvailable()) ? 0.4 : 1,
              display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'inherit',
            }} title={!event.attendees?.length ? 'No external attendees' : !isLocalAvailable() ? 'Local server required' : 'Generate brief'}>
              <Sparkles size={12} className={busy ? 'spin' : ''} />
              {busy ? 'Generating…' : 'Generate brief'}
            </button>
          )}
        </div>
      </div>

      <AnimatePresence>
        {open && hasBrief && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={springs.gentle}
            style={{ overflow: 'hidden' }}
          >
            <div style={{
              padding: '18px 22px 22px',
              borderTop: '1px solid rgba(0,0,0,0.05)',
              background: 'rgba(255,255,255,0.4)',
            }}>
              <Md>{savedBrief.brief_markdown}</Md>
              {savedBrief.generated_at && (
                <div style={{ fontSize: 10, color: T.textLight, marginTop: 12 }}>
                  Generated {new Date(savedBrief.generated_at).toLocaleString('en-GB')}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </Glass>
  );
};

export const BriefsView = ({ meetingBriefs, onUpdate }) => {
  const [hours, setHours] = useState(72);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);

  const loadUpcoming = useCallback(async () => {
    if (!isLocalAvailable()) {
      setError('Local server required to fetch calendar');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`/briefs/upcoming?hours=${hours}`);
      if (!res.ok) throw new Error(res.status === 401 ? 'Unauthorized — REACT_APP_EXEC_HQ_KEY not set or wrong' : `Status ${res.status}`);
      const data = await res.json();
      setEvents(data.events || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [hours]);

  useEffect(() => { loadUpcoming(); }, [loadUpcoming]);

  const briefByEventId = useMemo(() => {
    const map = new Map();
    for (const b of meetingBriefs || []) map.set(b.gcal_event_id, b);
    return map;
  }, [meetingBriefs]);

  const generate = async (eventId, force) => {
    if (!isLocalAvailable()) return;
    setGeneratingId(eventId);
    try {
      const res = await apiFetch(`/briefs/generate`, {
        method: 'POST',
        body: JSON.stringify({ gcal_event_id: eventId, force }),
      });
      if (!res.ok) throw new Error(`Status ${res.status}`);
      onUpdate();
    } catch (err) {
      console.error('[Briefs] Generate failed:', err);
    } finally {
      setGeneratingId(null);
    }
  };

  const stats = useMemo(() => {
    const total = events.length;
    const withBrief = events.filter(e => briefByEventId.has(e.gcal_event_id)).length;
    return { total, withBrief, missing: total - withBrief };
  }, [events, briefByEventId]);

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={springs.gentle}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 700, color: T.text, letterSpacing: '-0.03em' }}>Pre-Meeting Briefs</h1>
          <p style={{ fontSize: 13, color: T.textMuted, marginTop: 4 }}>
            {stats.total} upcoming meeting{stats.total !== 1 ? 's' : ''}
            {stats.withBrief > 0 && <span> · {stats.withBrief} briefed</span>}
            {stats.missing > 0 && <span style={{ color: T.violet, fontWeight: 600 }}> · {stats.missing} need brief</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={hours} onChange={e => setHours(parseInt(e.target.value, 10))} style={{
            padding: '7px 10px', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)',
            background: 'rgba(255,255,255,0.7)', fontSize: 12, fontFamily: 'inherit', color: T.text, outline: 'none',
          }}>
            <option value={24}>Next 24h</option>
            <option value={72}>Next 3 days</option>
            <option value={168}>Next 7 days</option>
          </select>
          <button onClick={loadUpcoming} disabled={loading} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', borderRadius: 10, border: 'none',
            background: T.blueLight, color: T.blue, cursor: 'pointer',
            fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
            opacity: loading ? 0.5 : 1,
          }}>
            <RefreshCw size={12} className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <Glass depth={0} style={{ padding: 16, marginBottom: 14 }} hover={false}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.red, fontSize: 13 }}>
            <AlertCircle size={14} /> {error}
          </div>
        </Glass>
      )}

      {!error && !loading && events.length === 0 && (
        <Glass depth={0} style={{ padding: 40, textAlign: 'center' }} hover={false}>
          <Calendar size={32} color={T.textLight} style={{ margin: '0 auto 12px', display: 'block' }} />
          <div style={{ fontSize: 14, color: T.textMuted }}>No upcoming meetings in this window.</div>
        </Glass>
      )}

      {events.map(e => (
        <BriefRow
          key={e.gcal_event_id}
          event={e}
          savedBrief={briefByEventId.get(e.gcal_event_id)}
          onGenerate={generate}
          busy={generatingId === e.gcal_event_id}
        />
      ))}
    </motion.div>
  );
};
